import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  Alert
} from '@mui/material';

import UploadStep from './components/UploadStep';
import GenerateStep from './components/GenerateStep';
import ReviewStep from './components/ReviewStep';
import ExportStep from './components/ExportStep';

import api from '../../utils/axios';

const steps = [
  { label: 'Upload & Extract', description: 'Upload documents and extract key information' },
  { label: 'Generate Draft', description: 'Create appeal letter with AI assistance' },
  { label: 'Review & Edit', description: 'Review and customize your appeal' },
  { label: 'Export & Send', description: 'Download or send your final appeal' }
];

const MinimalAppealPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { appealId } = useParams();

  const [activeStep, setActiveStep] = useState(0);
  const [appeal, setAppeal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const createNewAppeal = useCallback(async () => {
    try {
      setLoading(true);
      const prefill = location.state?.prefill || {};
      const response = await api.post('/documents/minimal-appeals/', {
        patient_name: prefill.patient_name || '',
        payer_name: prefill.payer_name || '',
        claim_number: prefill.claim_number || undefined,
        denial_code: prefill.denial_code || undefined,
        denial_text: prefill.denial_text || undefined,
      });
      setAppeal(response.data);

      // Navigate to the proper URL with appeal ID to maintain session
      if (response.data.id && !appealId) {
        navigate(`/appeals/${response.data.id}`, {
          replace: true,
          state: location.state,
        });
      }

      setError(null);
    } catch (err) {
      setError('Failed to create new appeal');
      console.error('Error creating appeal:', err);
    } finally {
      setLoading(false);
    }
  }, [appealId, location.state, navigate]);

  const loadAppeal = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/documents/minimal-appeals/${appealId}/`);
      setAppeal(response.data);

      // Determine which step to show based on appeal state
      if (response.data.letter && response.data.letter.content) {
        setActiveStep(2); // Review step (was 3)
      } else if (response.data.documents && response.data.documents.length > 0) {
        const hasOcrResults = response.data.documents.some(doc =>
          doc.ocr_status === 'success' || doc.extraction_metadata
        );
        setActiveStep(hasOcrResults ? 1 : 0); // Generate (was 2) or Upload+Extract
      } else {
        setActiveStep(0);
      }

      setError(null);
    } catch (err) {
      setError('Failed to load appeal');
      console.error('Error loading appeal:', err);
    } finally {
      setLoading(false);
    }
  }, [appealId]);

  // Load existing appeal if appealId is provided
  useEffect(() => {
    if (appealId) {
      loadAppeal();
    } else {
      createNewAppeal();
    }
  }, [appealId, createNewAppeal, loadAppeal]);

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
    setError(null);
    setSuccess(null);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setError(null);
    setSuccess(null);
  };

  const handleStepClick = (stepIndex) => {
    // Allow navigation to previous steps or current step
    if (stepIndex <= activeStep) {
      setActiveStep(stepIndex);
      setError(null);
      setSuccess(null);
    }
  };

  const handleAppealUpdate = (updatedAppeal) => {
    setAppeal(updatedAppeal);
  };

  const handleError = (errorMessage) => {
    setError(errorMessage);
    setSuccess(null);
  };

  const handleSuccess = (successMessage) => {
    setSuccess(successMessage);
    setError(null);
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <UploadStep
            appeal={appeal}
            onNext={handleNext}
            onUpdate={handleAppealUpdate}
            onError={handleError}
            onSuccess={handleSuccess}
          />
        );
      case 1:
        return (
          <GenerateStep
            appeal={appeal}
            onNext={handleNext}
            onBack={handleBack}
            onUpdate={handleAppealUpdate}
            onError={handleError}
            onSuccess={handleSuccess}
          />
        );
      case 2:
        return (
          <ReviewStep
            appeal={appeal}
            onNext={handleNext}
            onBack={handleBack}
            onUpdate={handleAppealUpdate}
            onError={handleError}
            onSuccess={handleSuccess}
          />
        );
      case 3:
        return (
          <ExportStep
            appeal={appeal}
            onBack={handleBack}
            onUpdate={handleAppealUpdate}
            onError={handleError}
            onSuccess={handleSuccess}
            onComplete={() => navigate('/appeals')}
          />
        );
      default:
        return 'Unknown step';
    }
  };

  if (loading && !appeal) {
    return (
      <Container sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Sticky Status Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, position: 'sticky', top: 16, zIndex: 10, boxShadow: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3, position: 'sticky', top: 16, zIndex: 10, boxShadow: 3 }}>
          {success}
        </Alert>
      )}

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" fontWeight={600} gutterBottom>
            Quick Appeal Generator
          </Typography>

          <Typography variant="body2" color="text.secondary">
            Upload documents and generate professional appeal letters in minutes
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }} alternativeLabel>
          {steps.map((step, index) => (
            <Step
              key={step.label}
              completed={index < activeStep}
            >
              <StepLabel
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: index <= activeStep ? 'pointer' : 'default',
                  '&:hover': index <= activeStep ? { opacity: 0.7 } : {},
                }}
                optional={
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                }
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <Box sx={{ mt: 4 }}>
          {renderStepContent(activeStep)}
        </Box>
      </Paper>
    </Container>
  );
};

export default MinimalAppealPage;
